import { useIsEditorContext } from "@editor-providers";
import { useAppDispatch } from "@store";
import { editEntity, setVersionDataValue } from "@store/story";
import { setBoundOrFieldValue } from "@store/story/thunks/set-bound-or-field-value";
import { produce } from "immer";
import { useCallback, useRef } from "react";
import { useUploadMedia } from "@/shared";
import type { IMediaUploadResponse } from "@/shared/types";
import { getSoleTokenName, prepBlurhashUrl } from "@/shared/utils";

type UploadArrayItemResult = {
  index: number;
  url: string;
  originalUrl: string;
};

// Module-level so all per-element hook instances share it: when the same
// upload=true URL is uploaded by N duplicated elements at once, they coalesce
// into ONE upload. Entries are evicted on settle, so this only dedupes
// concurrent uploads — persistent/cross-session dedup is the backend hash
// precheck's job (Layer 3), which is org-scoped and correct.
const inFlightUploads = new Map<string, Promise<IMediaUploadResponse>>();

const useUploadFromQueryParam = () => {
  const dispatch = useAppDispatch();
  const isEditor = useIsEditorContext();
  const { uploadMediaFromUrl } = useUploadMedia();
  // Per-entity stale-write guard: the latest URL each entity asked us to upload.
  const latestUrlByEntity = useRef<Map<string, string>>(new Map());
  const currentData = useRef<Record<string, unknown>[] | null>(null);

  const dedupedUploadFromUrl = useCallback(
    (
      url: string,
      opts?: { metadata?: Record<string, unknown> },
    ): Promise<IMediaUploadResponse> => {
      // Keyed by URL only: dedup assumes a given source URL always yields the
      // same asset, so its metadata is identical across callers. The first
      // caller's opts win for any concurrent reuse of the same URL.
      let promise = inFlightUploads.get(url);
      if (!promise) {
        promise = uploadMediaFromUrl(url, opts);
        inFlightUploads.set(url, promise);
        // Evict on settle (success or failure) so this map only ever holds
        // in-flight uploads — no long-lived cross-story/cross-org state. The
        // trailing no-op catch keeps this side-channel from surfacing the
        // upload's rejection as an unhandled one; callers handle the rejection
        // on the original `promise` we return.
        promise.finally(() => inFlightUploads.delete(url)).catch(() => {});
      }
      return promise;
    },
    [uploadMediaFromUrl],
  );

  const handleUpload = useCallback(
    async ({
      url,
      metadata,
      id,
      property,
      bindingKey,
    }: {
      url: string;
      id: string;
      property: string;
      metadata?: Record<string, unknown>;
      /**
       * If set, the final URL is routed through `setBoundOrFieldValue` so
       * it lands in `version.data[var]` when the entity has a sole-token
       * binding for this field. Otherwise behaves exactly as before.
       */
      bindingKey?: string;
    }) => {
      if (!isEditor || !canUpload(url)) return;
      latestUrlByEntity.current.set(id, url);

      try {
        const resp = await dedupedUploadFromUrl(url, { metadata });
        let newUrl = resp.url;
        let newMetadata = resp.metadata;

        if (resp.typeGroup === "image") {
          const { imageBlurhash, ...restMetadata } = resp.metadata || {};
          if (imageBlurhash) {
            newUrl = prepBlurhashUrl(resp.url, imageBlurhash);
            newMetadata = restMetadata;
          }
        }

        // Only update if the URL hasn't changed since upload started
        if (latestUrlByEntity.current.get(id) === url) {
          dispatch(
            setBoundOrFieldValue({
              entityId: id,
              key: property,
              bindingKey,
              value: newUrl,
              extra: { metadata: newMetadata },
              skipHistory: true,
            }),
          );
          latestUrlByEntity.current.delete(id); // Reset after successful update
        }
      } catch {
        if (latestUrlByEntity.current.get(id) === url) {
          dispatch(
            setBoundOrFieldValue({
              entityId: id,
              key: property,
              bindingKey,
              value: setRetry(url),
              skipHistory: true,
            }),
          );
          latestUrlByEntity.current.delete(id);
        }
      }
    },
    [dispatch, isEditor, dedupedUploadFromUrl],
  );

  const handleUploadArrayItem = useCallback(
    async (
      item: Record<string, unknown>,
      index: number,
      key: string,
    ): Promise<UploadArrayItemResult> => {
      const itemUrl = item[key] as string;
      if (!isEditor || !canUpload(itemUrl))
        return { index, url: "", originalUrl: itemUrl };
      try {
        const resp = await dedupedUploadFromUrl(itemUrl);
        let newUrl = resp.url;
        if (resp.typeGroup === "image") {
          const { imageBlurhash } = resp.metadata || {};
          if (imageBlurhash) {
            newUrl = prepBlurhashUrl(resp.url, imageBlurhash);
          }
        }
        return { index, url: newUrl, originalUrl: itemUrl };
      } catch {
        return { index, url: setRetry(itemUrl), originalUrl: itemUrl };
      }
    },
    [isEditor, dedupedUploadFromUrl],
  );

  const handleArrayUpload = useCallback(
    async ({
      data,
      id,
      property,
      key,
      itemBindingKey,
    }: {
      id: string;
      property: string;
      data: Record<string, unknown>[];
      key: string;
      /**
       * If set, names a per-item field that may hold a `{{var}}` binding
       * (e.g. "variable" for grid items). For items whose binding is a
       * sole-token, the new URL is routed to `version.data[var]` instead
       * of being written into the array — preserving the template fallback.
       */
      itemBindingKey?: string;
    }) => {
      if (!isEditor) return;
      currentData.current = data;
      const promises = data.map((item, index) =>
        handleUploadArrayItem(item, index, key),
      );
      const results = await Promise.allSettled(promises);
      let updated = false;
      const versionUpdates: Array<{ key: string; value: string }> = [];

      const newData = produce(currentData.current, (draft) => {
        for (const result of results) {
          if (result.status !== "fulfilled") continue;
          const { index, url, originalUrl } = result.value;
          if (!url) continue;
          if (!currentData.current) return;
          const currentDataUrl = currentData.current[index][key] as string;
          if (currentDataUrl !== originalUrl) continue;

          const boundVar = itemBindingKey
            ? getSoleTokenName(
                currentData.current[index][itemBindingKey] as
                  | string
                  | undefined,
              )
            : null;

          if (boundVar) {
            // Bound item — push to version.data, leave the array fallback as-is.
            versionUpdates.push({ key: boundVar, value: url });
          } else {
            draft[index][key] = url;
            updated = true;
          }
        }
      });

      for (const { key: varKey, value } of versionUpdates) {
        dispatch(setVersionDataValue({ key: varKey, value }));
      }
      if (updated) {
        dispatch(
          editEntity({
            skipHistory: true,
            id,
            data: {
              [property]: newData,
            },
          }),
        );
      }
      currentData.current = null;
    },
    [dispatch, isEditor, handleUploadArrayItem],
  );
  const cancelUpload = useCallback(() => {
    latestUrlByEntity.current.clear();
  }, []);

  return { handleUpload, handleArrayUpload, cancelUpload };
};

export default useUploadFromQueryParam;

const canUpload = (url: string | undefined) => {
  return url?.includes("upload=true") && !url?.includes("retry=3");
};

const setRetry = (url: string) => {
  const urlObj = new URL(url, window.location.origin);
  const retry = Number(urlObj.searchParams.get("retry") || 0);
  if (retry >= 3) return url;
  urlObj.searchParams.set("retry", String(retry + 1));
  return urlObj.toString();
};
